import { useState, useCallback, useRef } from "react";
import { getAnalytics } from "@/hooks/useDi";
import type { Toast, ToastOptions } from "@/stores/toastTypes";

export type { Toast, ToastOptions } from "@/stores/toastTypes";

export const useToast = () => {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const counterRef = useRef(0);

	const showToastCallback = useCallback(
		((
			messageOrOptions: string | ToastOptions,
			type: Toast["type"] = "info",
			duration = 3000,
		): string => {
			counterRef.current += 1;
			const id = `${Date.now()}-${counterRef.current}`;
			let toast: Toast;

			if (typeof messageOrOptions === "string") {
				toast = { id, message: messageOrOptions, type, duration };
			} else {
				const options = messageOrOptions;
				toast = {
					id,
					message: options.message,
					type: options.type,
					title: options.title,
					duration: options.duration ?? 3000,
				};
			}

			setToasts((prev) => [...prev, toast]);

			if (toast.type === "error" || toast.type === "warning") {
				getAnalytics().trackUserFacingError({
					message: toast.message,
					title: toast.title,
					type: toast.type,
				});
			}

			setTimeout(() => {
				setToasts((prev) => prev.filter((t) => t.id !== id));
			}, toast.duration);

			return id;
		}) as {
			(options: ToastOptions): string;
			(message: string, type?: Toast["type"], duration?: number): string;
		},
		[],
	);

	const dismissToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((t) => t.id !== id));
	}, []);

	return {
		toasts,
		showToast: showToastCallback,
		dismissToast,
	};
};
